import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Primary label rendered next to the box. */
  label?: ReactNode;
  /** Optional secondary line under the label. */
  description?: ReactNode;
}

/**
 * A styled checkbox with an optional label + description, wrapping a native
 * `<input type="checkbox">` so it stays keyboard- and screen-reader-accessible.
 * Colored with the `iris` accent token so it flips with the theme.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, disabled, className = '', ...props },
  ref,
) {
  return (
    <label
      className={`flex items-start gap-2.5 ${
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
      } ${className}`}
    >
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        className="mt-0.5 h-4 w-4 flex-none rounded-sm border border-border bg-surface accent-iris focus:outline-none focus-visible:ring-2 focus-visible:ring-iris"
        {...props}
      />
      {(label || description) && (
        <span className="min-w-0">
          {label && <span className="block truncate text-sm text-bone">{label}</span>}
          {description && <span className="block text-xs text-muted">{description}</span>}
        </span>
      )}
    </label>
  );
});
